import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { getThemeColors, spacing, fontSize, fontWeight, borderRadius, shadows } from '../styles/theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getSystemTheme = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const resolveTheme = (pref) => pref === 'system' ? getSystemTheme() : pref;

export const ThemeProvider = ({ children }) => {
  const [themePref, setThemePref] = useState(() => localStorage.getItem('kmfc-theme') || 'system');
  const [themeMode, setThemeMode] = useState(() => resolveTheme(localStorage.getItem('kmfc-theme') || 'system'));
  const [loading, setLoading] = useState(false);

  const applyTheme = (resolved) => {
    const colors = getThemeColors(resolved);
    document.documentElement.setAttribute('data-theme', resolved);
    document.body.style.backgroundColor = colors.background;
  };

  useEffect(() => {
    applyTheme(themeMode);
  }, [themeMode]);

  // Watch system preference changes when pref is 'system'
  useEffect(() => {
    if (themePref !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setThemeMode(getSystemTheme());
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themePref]);

  const setThemeHandler = (newPref) => {
    const resolved = resolveTheme(newPref);
    setThemePref(newPref);
    setThemeMode(resolved);
    localStorage.setItem('kmfc-theme', newPref);
  };

  const theme = {
    themeMode,
    themePref,
    colors: getThemeColors(themeMode),
    setTheme: setThemeHandler,
    loading,
  };

  const styledTheme = {
    mode: themeMode,
    colors: getThemeColors(themeMode),
    spacing,
    fontSize,
    fontWeight,
    borderRadius,
    shadows,
  };

  return (
    <ThemeContext.Provider value={theme}>
      <StyledThemeProvider theme={styledTheme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
